import {NetWorthRole, useNetWorthInvestmentSplitQuery, useNetWorthQuery} from '@transactions/graphql';
import {parseDate, today} from 'common/date';
import {ChevronRight, Eye, EyeOff} from 'lucide-react';
import * as React from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {TAX_TREATMENTS} from 'src/InvestmentAccountsView/holdingsModel';
import {cn} from 'src/lib/utils';
import {NetWorthChart, NetWorthChartSkeleton} from 'src/NetWorthView/NetWorthChart';
import {
  formatPercentChange,
  formatShare,
  formatSignedDollars,
  formatWholeDollars,
  type GroupMeta,
  groupMetaOf,
  MASK,
  metaFor,
  percentChange,
} from 'src/NetWorthView/netWorthFormat';
import {netWorthViewModel} from 'src/NetWorthView/netWorthModel';
import {
  ALL_TIME_START,
  customStart,
  DEFAULT_NET_WORTH_PERIOD,
  NET_WORTH_PERIODS,
  type NetWorthPeriodKey,
  netWorthPeriodByKey,
  periodChangeLabel,
  periodStart,
} from 'src/NetWorthView/netWorthPeriods';
import {netWorthSplitLines} from 'src/NetWorthView/netWorthSplit';
import {useRevealed} from 'src/NetWorthView/useRevealed';
import {Segmented} from 'src/Segmented';
import type {NetWorthQuery} from '@transactions/graphql';

type NetWorth = NetWorthQuery['netWorth'];

/** A change as dollars and percent, or — hidden — percent (or direction). */
const changeText = (delta: number, base: number, revealed: boolean): string => {
  const ratio = percentChange(delta, base);
  if (revealed) {
    return ratio != null
      ? `${formatSignedDollars(delta)} (${formatPercentChange(ratio)})`
      : formatSignedDollars(delta);
  }
  // Hidden and with no meaningful percentage, direction is all that's left.
  if (ratio != null) return formatPercentChange(ratio);
  return delta > 0 ? 'Up' : delta < 0 ? 'Down' : '';
};

const changeClass = (delta: number) =>
  cn(
    'tabular-nums',
    delta > 0 && 'text-positive',
    delta < 0 && 'text-destructive',
  );

const Change = ({
  delta,
  base,
  label,
  revealed,
}: {
  delta: number;
  base: number;
  label: string;
  revealed: boolean;
}) => (
  <div className="text-sm">
    <span className={changeClass(delta)}>{changeText(delta, base, revealed)}</span>{' '}
    <span className="text-muted-foreground">{label}</span>
  </div>
);

/**
 * The groups in the URL's `hide` list (lowercase roles), in role order. An
 * older link's `stock=out` (from before any group could be hidden) still
 * hides company stock when there is no `hide` list.
 */
const hiddenGroupsOf = (params: URLSearchParams): NetWorthRole[] => {
  const hide = params.get('hide');
  const tokens = new Set(
    hide == null
      ? params.get('stock') === 'out'
        ? [NetWorthRole.CompanyStock.toLowerCase()]
        : []
      : hide.split(',').map((token) => token.trim()),
  );
  return Object.values(NetWorthRole).filter((role) => tokens.has(role.toLowerCase()));
};

/**
 * The period's change by where it came from: what investments did, from
 * the gains split, and everything else (the rest of the change, which
 * includes what investments did before their records began; the heading
 * says when that is). Amounts follow the reveal (hidden, each reads as a
 * percent of where the period started).
 */
const ChangeSplit = ({
  from,
  to,
  change,
  base,
  revealed,
}: {
  from: string;
  to: string;
  /** The period's change in net worth; null when it has no start. */
  change: number | null;
  base: number;
  revealed: boolean;
}) => {
  const {data, error, refetch} = useNetWorthInvestmentSplitQuery({
    variables: {from, to},
    fetchPolicy: 'cache-and-network',
  });
  if (data == null && error != null) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        Couldn't load investment returns.
        <button type="button" className="underline" onClick={() => void refetch()}>
          Retry
        </button>
      </div>
    );
  }
  if (data == null) return null;
  const split = data.netWorthInvestmentSplit;
  const lines = netWorthSplitLines(split, change);
  // What the parts don't cover, in full: when investment records start and
  // when some stop (each its own note: they can be different accounts'),
  // and which accounts aren't split, don't reconcile, or rest on estimates.
  const day = (iso: string) => parseDate(iso).toFormat('MMM d, yyyy');
  const notes = [
    split.recordsFrom != null ? `Investment records from ${day(split.recordsFrom)}` : null,
    split.recordsThrough != null ? `Some investment records end ${day(split.recordsThrough)}` : null,
    split.unsplit.length > 0 ? `Not split: ${split.unsplit.join(', ')}` : null,
    split.unreconciled.length > 0 ? `Not reconciled: ${split.unreconciled.join(', ')}` : null,
    split.estimated.length > 0 ? `Estimated: ${split.estimated.join(', ')}` : null,
  ].filter((note) => note != null);
  return (
    <section aria-label="Change by source" className="flex flex-col gap-1.5">
      <h2 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">By source</h2>
      {notes.length > 0 && <p className="text-muted-foreground text-xs">{notes.join(' · ')}</p>}
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_auto_auto_auto] sm:justify-start">
        {lines.map((line) => (
          <React.Fragment key={line.key}>
            <dt className="text-muted-foreground">{line.label}</dt>
            <dd className={line.cents == null || line.cents === 0 ? 'text-muted-foreground' : changeClass(line.cents)}>
              {line.cents == null
                ? '—'
                : line.cents === 0
                  ? revealed
                    ? '$0'
                    : '0%'
                  : changeText(line.cents, base, revealed)}
            </dd>
          </React.Fragment>
        ))}
      </dl>
      {split.byTreatment.length > 0 && (
        <details className="group text-sm">
          <summary className="text-muted-foreground flex w-fit cursor-pointer list-none items-center gap-1 text-xs [&::-webkit-details-marker]:hidden">
            <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" />
            By tax treatment
          </summary>
          <div className="mt-1.5 grid w-fit grid-cols-[auto_auto_auto_auto] gap-x-6 gap-y-1">
            <span />
            <span className="text-muted-foreground text-xs">Realized</span>
            <span className="text-muted-foreground text-xs">Unrealized</span>
            <span className="text-muted-foreground text-xs">Income</span>
            {split.byTreatment.map((row) => (
              <React.Fragment key={row.taxTreatment}>
                <span className="text-muted-foreground">
                  {TAX_TREATMENTS.find((treatment) => treatment.key === row.taxTreatment)?.label ?? row.taxTreatment}
                </span>
                {(
                  [
                    ['realized', row.realized],
                    ['unrealized', row.unrealized],
                    ['income', row.income],
                  ] as const
                ).map(([part, cents]) => (
                  <span
                    key={part}
                    className={cents == null || cents === 0 ? 'text-muted-foreground' : changeClass(cents)}
                  >
                    {cents == null ? '—' : cents === 0 ? (revealed ? '$0' : '0%') : changeText(cents, base, revealed)}
                  </span>
                ))}
              </React.Fragment>
            ))}
          </div>
        </details>
      )}
    </section>
  );
};

/**
 * The chart's legend, one chip per group holding something — and its
 * filter: pressed, a group is drawn; unpressed, it comes off the chart.
 * Only the chart: the headline and the groups below always count
 * everything.
 */
const GroupLegend = ({
  groups,
  hidden,
  meta,
  onToggle,
}: {
  groups: NetWorth['groups'];
  hidden: NetWorthRole[];
  meta: Map<NetWorthRole, GroupMeta>;
  onToggle: (role: NetWorthRole) => void;
}) => {
  // Even a lone group gets its chip: it is the only way back from a link
  // that hides it.
  if (groups.length === 0) return null;
  return (
    <div role="group" aria-label="Groups" className="flex flex-wrap gap-1.5">
      {groups.map((group) => {
        const excluded = hidden.includes(group.role);
        return (
          <button
            key={group.role}
            type="button"
            aria-pressed={!excluded}
            onClick={() => onToggle(group.role)}
            className={cn(
              'border-border inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition',
              excluded
                ? 'text-muted-foreground border-dashed'
                : 'bg-muted/60 text-foreground',
            )}
          >
            <span
              className="size-2 rounded-full border"
              style={{
                borderColor: metaFor(meta, group.role).color,
                backgroundColor: excluded
                  ? 'transparent'
                  : metaFor(meta, group.role).color,
              }}
            />
            {group.label}
          </button>
        );
      })}
    </div>
  );
};

const RoleGroup = ({
  title,
  total,
  roles,
  meta,
  changeByRole,
  revealed,
}: {
  title: string;
  total: number;
  roles: NetWorth['roles'];
  meta: Map<NetWorthRole, GroupMeta>;
  changeByRole: Map<NetWorthRole, {start: number; end: number}>;
  revealed: boolean;
}) => {
  if (roles.length === 0) return null;
  const groupTotal = Math.abs(total);
  return (
    <section className="flex flex-col">
      <div className="text-muted-foreground border-border flex items-baseline justify-between border-b pb-1.5 text-xs font-medium tracking-wide uppercase">
        <span>{title}</span>
        <span className="tabular-nums">
          {revealed ? formatWholeDollars(total) : MASK}
        </span>
      </div>
      {roles.map((role) => {
        const share = groupTotal === 0 ? 0 : Math.abs(role.total) / groupTotal;
        // A group's change over the period, on the side its net sits on (a
        // group split across both sides would otherwise show it twice) —
        // and only once it existed at the period's start: a group that
        // started from nothing has no change worth a number.
        const change = changeByRole.get(role.role);
        const delta =
          change && change.start !== 0 && Math.sign(change.end) === Math.sign(role.total)
            ? change.end - change.start
            : 0;
        return (
          <Link
            key={role.role}
            to={metaFor(meta, role.role).href}
            className="border-border hover:bg-muted-foreground/10 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 border-b px-1 py-2.5"
          >
            <span className="flex min-w-0 items-baseline gap-1.5">
              <span className="truncate text-sm font-medium">
                {metaFor(meta, role.role).label}
              </span>
              <span className="text-muted-foreground text-xs">
                {role.accountCount}
              </span>
            </span>
            <span className="flex items-baseline justify-end gap-2 text-sm tabular-nums">
              {delta !== 0 && change && (
                <span className={cn('text-xs', changeClass(delta))}>
                  {changeText(delta, change.start, revealed)}
                </span>
              )}
              <span>{revealed ? formatWholeDollars(role.total) : formatShare(share)}</span>
            </span>
            <span className="bg-muted col-span-2 h-1.5 overflow-hidden rounded-full">
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${Math.max(share * 100, share > 0 ? 1 : 0)}%`,
                  backgroundColor: metaFor(meta, role.role).color,
                }}
              />
            </span>
          </Link>
        );
      })}
    </section>
  );
};

const AccountNote = ({
  title,
  detail,
  accounts,
}: {
  title: string;
  detail: string;
  accounts: NetWorth['unvaluedAccounts'];
}) => {
  if (accounts.length === 0) return null;
  return (
    <div className="border-border rounded-md border p-3 text-sm">
      <div className="font-medium">{title}</div>
      <p className="text-muted-foreground mt-0.5 text-xs">{detail}</p>
      <ul className="mt-2 flex flex-col gap-1">
        {accounts.map((account) => (
          <li key={account.accountId}>
            <Link
              to={
                account.role === NetWorthRole.CompanyStock
                  ? '/equity'
                  : `/accounts/${account.accountId}`
              }
              className="min-w-0 truncate underline-offset-2 hover:underline"
            >
              {account.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Bar = ({className}: {className: string}) => (
  <div className={cn('bg-muted/60 animate-pulse rounded', className)} />
);

/** The page's shape while its one load is in flight, so nothing jumps. */
const NetWorthSkeleton = () => (
  <div aria-busy="true" aria-label="Loading net worth" className="flex flex-col gap-6">
    <div className="flex items-start justify-between gap-3">
      <div className="flex flex-col gap-2">
        <Bar className="h-3 w-20" />
        <Bar className="h-8 w-44" />
      </div>
      <Bar className="h-9 w-20 rounded-lg" />
    </div>
    <div className="-mt-3 flex flex-col gap-1.5">
      <Bar className="h-4 w-48" />
      <Bar className="h-4 w-56" />
    </div>
    <div className="flex flex-col gap-3">
      <Bar className="h-7 w-80 max-w-full rounded-lg" />
      <div className="flex flex-wrap gap-1.5">
        {['w-20', 'w-24', 'w-28', 'w-16', 'w-24'].map((width, index) => (
          <Bar key={index} className={cn('h-5 rounded-full', width)} />
        ))}
      </div>
      <NetWorthChartSkeleton />
    </div>
    <div className="flex flex-col gap-3">
      {[0, 1, 2, 3].map((index) => (
        <Bar key={index} className="h-10 w-full" />
      ))}
    </div>
  </div>
);

/**
 * Net worth: every account's value summed, its history over a chosen
 * period, and where it sits. A destination rather than the landing page, and
 * hidden until asked — amounts stay masked (shape and percentages still
 * show) until revealed, and a reveal lasts only the browser session.
 *
 * One load brings all of history; the period and the groups the chart
 * shows are worked out here from it, so changing either is instant. Hiding
 * a group affects only the chart — the headline, its changes, and the
 * groups below always count everything. Both live in the URL
 * (?period=3y&hide=retirement,company_stock, or
 * ?period=custom&since=2021-06), so a view can be linked and survives a
 * reload; the defaults keep a clean URL.
 */
export const NetWorthView = () => {
  useBreadcrumb({label: 'Net worth', reset: true});
  const [revealed, setRevealed] = useRevealed();
  const [searchParams, setSearchParams] = useSearchParams();
  const period = netWorthPeriodByKey(searchParams.get('period'));
  const since = searchParams.get('since');
  const hidden = React.useMemo(
    () => hiddenGroupsOf(searchParams),
    [searchParams],
  );
  const requestedStart = periodStart(period, today(), since);

  const setParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value == null) next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, {replace: true});
  };

  const {data, error, refetch} = useNetWorthQuery({
    variables: {startDate: ALL_TIME_START},
    fetchPolicy: 'cache-and-network',
  });

  const model = React.useMemo(
    () =>
      data == null
        ? null
        : netWorthViewModel(data.netWorth, {requestedStart}),
    [data, requestedStart],
  );
  const points = React.useMemo(
    () => (model == null ? [] : model.chartPoints(hidden)),
    [model, hidden],
  );

  if (data == null || model == null) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4">
          {error ? (
            <div className="flex flex-col items-start gap-3 text-sm">
              <span className="text-destructive">
                Couldn't load net worth: {error.message}
              </span>
              <Button variant="outline" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <NetWorthSkeleton />
          )}
        </div>
      </div>
    );
  }

  const netWorth = data.netWorth;
  const meta = groupMetaOf(netWorth.groups);
  const {total} = netWorth;
  const toggleGroup = (role: NetWorthRole) => {
    const nextHidden = hidden.includes(role)
      ? hidden.filter((other) => other !== role)
      : [...hidden, role];
    const hide = Object.values(NetWorthRole)
      .filter((value) => nextHidden.includes(value))
      .map((value) => value.toLowerCase())
      .join(',');
    // Once a chip is pressed, `hide` is the one source of truth.
    setParams({stock: null, hide: hide === '' ? null : hide});
  };
  const selectPeriod = (key: NetWorthPeriodKey) => {
    if (key === 'custom') {
      // Start from where the chart already begins.
      setParams({
        period: 'custom',
        since:
          since != null && customStart(since, today()) != null
            ? since
            : model.start.slice(0, 7),
      });
    } else {
      setParams({period: key === DEFAULT_NET_WORTH_PERIOD ? null : key, since: null});
    }
  };
  const lastMonthEnd =
    parseDate(netWorth.asOf).startOf('month').minus({days: 1}).toISODate() ?? '';
  const monthBase = model.totalOn(lastMonthEnd);
  const periodBase = model.startTotal;
  const assetRoles = netWorth.roles.filter((role) => role.total >= 0);
  const debtRoles = netWorth.roles.filter((role) => role.total < 0);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-3xl flex-col gap-6 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              Net worth
            </div>
            <button
              type="button"
              onClick={() => setRevealed(!revealed)}
              className="text-foreground mt-1 block text-left text-3xl font-semibold tracking-tight tabular-nums"
              aria-label={revealed ? 'Hide amounts' : 'Show amounts'}
            >
              {revealed ? formatWholeDollars(total) : MASK}
            </button>
          </div>
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={() => setRevealed(!revealed)}
            aria-pressed={revealed}
          >
            {revealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            {revealed ? 'Hide' : 'Show'}
          </Button>
        </div>

        <div className="-mt-3 flex flex-col gap-0.5">
          {monthBase != null && (
            <Change
              delta={total - monthBase}
              base={monthBase}
              label={`since ${parseDate(lastMonthEnd).toFormat('MMM d')}`}
              revealed={revealed}
            />
          )}
          {periodBase != null && points.length > 1 && (
            <Change
              delta={total - periodBase}
              base={periodBase}
              label={periodChangeLabel(period, requestedStart, model.start)}
              revealed={revealed}
            />
          )}
        </div>

        {/* Only beside the change it explains: with today alone there's none. */}
        {periodBase != null && points.length > 1 && (
          <ChangeSplit
            from={model.start}
            to={netWorth.asOf}
            change={total - periodBase}
            base={periodBase}
            revealed={revealed}
          />
        )}

        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2">
              <Segmented
                label="Period"
                options={NET_WORTH_PERIODS}
                value={period}
                onChange={selectPeriod}
              />
              {period === 'custom' && (
                <label className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                  Since
                  <input
                    type="month"
                    value={
                      since != null && customStart(since, today()) != null
                        ? since
                        : model.start.slice(0, 7)
                    }
                    max={netWorth.asOf.slice(0, 7)}
                    onChange={(event) => {
                      if (event.target.value !== '') {
                        setParams({since: event.target.value});
                      }
                    }}
                    className="border-border bg-background text-foreground rounded-md border px-2 py-0.5 text-xs"
                  />
                </label>
              )}
            </div>
          </div>
          <GroupLegend
            // Only groups the chart can draw: one listed without a series
            // (company stock that can't be valued) would be a dead toggle.
            groups={netWorth.groups.filter((group) =>
              netWorth.roleSeries.some((series) => series.role === group.role),
            )}
            hidden={hidden}
            meta={meta}
            onToggle={toggleGroup}
          />
          {points.length > 1 ? (
            <NetWorthChart
              points={points}
              roles={netWorth.roleSeries
                .map((series) => series.role)
                .filter((role) => !hidden.includes(role))}
              meta={meta}
              revealed={revealed}
            />
          ) : (
            <p className="text-muted-foreground py-10 text-center text-sm">
              No balance history yet — only today’s values are known.
            </p>
          )}
          <p className="text-muted-foreground text-xs">
            Brief spikes are usually money moving between two accounts whose
            balances were recorded on different days.
          </p>
        </section>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <RoleGroup
            title="Assets"
            total={assetRoles.reduce((sum, role) => sum + role.total, 0)}
            roles={assetRoles}
            meta={meta}
            changeByRole={model.changeByRole}
            revealed={revealed}
          />
          <RoleGroup
            title="Debts"
            total={debtRoles.reduce((sum, role) => sum + role.total, 0)}
            roles={debtRoles}
            meta={meta}
            changeByRole={model.changeByRole}
            revealed={revealed}
          />
        </div>

        <AccountNote
          title="Not counted"
          detail="Their dollar value is unknown: units with no dollar cost recorded behind them, or holdings priced in another currency."
          accounts={netWorth.unvaluedAccounts}
        />
      </div>
    </div>
  );
};
